import React, { useMemo } from 'react';
import moment from 'moment';
import FormControl from '@mui/material/FormControl';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import AlertComponent from '@ui/components/Alert/Alert.component';
import InputComponent from '@ui/components/Input/Input.component';
import SelectComponent from '@ui/components/Select/Select.component';
import DateInputComponent from '@ui/components/Date-input/Date-input.component';
import { IValue } from '@ui/components/Select/IValue';
import useDictionary from '@ui/hooks/useDictionary';
import IconSelectComponent from '../Icon-select/Icon-select.component';
import SaveConfirmComponent from '../Device-settings/Save-confirm.component';
import { itemTypes } from '@utils/getDeviceObjectKeys';
import { IProps } from './IProps';

const Component: React.FC<IProps> = ({
    lang,
    loading,
    imei,
    groupId,
    groups,
    type,
    types,
    additionalInfo,
    keys,
    oldDeviceName,
    deviceName,
    deviceIconNum,
    deviceNameError,
    iconSelectVisible,
    iconSelectEl,
    serverError,
    demoModeError,
    iconSelectRef,
    savedAlertVisible,
    onDeviceNameChange,
    onSaveClick,
    onInputClick,
    onCancelClick,
    onSelectIconBtnClick,
    onSelectIconWindowClose,
    onIconClick,
    onKeyDown,
    onBtnKeyDown,
    onGroupChange,
    onTypeChange,
    onAdditionalInfoChange,
    onAdditionalInfoDateChange,
    onSaveAlertClose,
}: IProps) => {
    const getWord = useDictionary();

    const alertMessage = useMemo(() => {
        if (serverError) return 'serverError';
        else if (demoModeError) return 'demoModeError';
        else return '';
    }, [serverError, demoModeError]);

    const groupsList: IValue[] = useMemo(
        () => groups.map(({ id: value, name: label }) => ({ value, label })),
        [groups],
    );

    const additionalInfoElements = useMemo(() => {
        const additionalInfoList = keys.map((item) => {
            switch (item.type) {
                case itemTypes.date:
                    const val =
                        additionalInfo[
                            item.name as keyof typeof additionalInfo
                        ];
                    return (
                        <DateInputComponent
                            lang={lang}
                            label={item.name}
                            id={item.name}
                            key={item.name}
                            timezone={0}
                            value={val ? moment(val, 'DD.MM.YYYY') : null}
                            onChange={onAdditionalInfoDateChange.bind(
                                this,
                                item.name,
                            )}
                            onClick={onInputClick}
                            onFocus={onInputClick}
                            onKeyDown={onKeyDown}
                        />
                    );
                default:
                    return (
                        <InputComponent
                            label={item.name}
                            id={item.name}
                            key={item.name}
                            lang={lang}
                            name={item.name}
                            value={
                                additionalInfo[
                                    item.name as keyof typeof additionalInfo
                                ] ?? ''
                            }
                            onChange={onAdditionalInfoChange}
                            onClick={onInputClick}
                            onFocus={onInputClick}
                            onKeyDown={onKeyDown}
                        />
                    );
            }
        });
        let additionalInfoElements: JSX.Element[] = [];
        for (let i = 0; i < additionalInfoList.length; i++) {
            if ((i + 1) % 2) continue;
            const a = additionalInfoList[i];
            const b = additionalInfoList[i + 1];
            additionalInfoElements.push(
                <Box className="form-row">
                    {a}
                    {b}
                </Box>,
            );
        }
        return additionalInfoElements;
    }, [
        keys,
        lang,
        additionalInfo,
        onAdditionalInfoChange,
        onInputClick,
        onKeyDown,
    ]);

    return (
        <>
            <Box className="edit-device__wrap">
                <Box className="edit-device">
                    <IconButton
                        className="close-btn"
                        onClick={onCancelClick}
                        onKeyDown={onBtnKeyDown}
                    >
                        <CloseIcon className="close-btn__ico" />
                    </IconButton>
                    {!loading && (
                        <>
                            <Box className="edit-device__header">
                                <Box className="edit-device__title-wrap">
                                    <Typography
                                        className="edit-device__title"
                                        variant="h6"
                                    >
                                        {getWord('editDeviceDescriptionTitle')}
                                    </Typography>
                                    <Typography
                                        className="edit-device__title-text"
                                        variant="body1"
                                    >
                                        {`${oldDeviceName} [${imei}]`}
                                    </Typography>
                                </Box>
                                <IconSelectComponent
                                    deviceIconNum={deviceIconNum}
                                    iconSelectVisible={iconSelectVisible}
                                    iconSelectEl={iconSelectEl}
                                    iconSelectRef={iconSelectRef}
                                    onSelectIconBtnClick={onSelectIconBtnClick}
                                    onSelectIconWindowClose={
                                        onSelectIconWindowClose
                                    }
                                    onIconClick={onIconClick}
                                />
                            </Box>
                            <AlertComponent
                                variant="modal"
                                severity="error"
                                lang={lang}
                                message={alertMessage}
                                display={!!alertMessage}
                            />
                            <FormControl
                                className="edit-device__form"
                                component="form"
                            >
                                <Box className="form-row">
                                    <InputComponent
                                        label="deviceName"
                                        name="device-name"
                                        type="text"
                                        errorMessage="deviceNameError"
                                        lang={lang}
                                        value={deviceName}
                                        error={deviceNameError}
                                        onChange={onDeviceNameChange}
                                        onClick={onInputClick}
                                        onFocus={onInputClick}
                                        onKeyDown={onKeyDown}
                                    />
                                    <SelectComponent
                                        label="group"
                                        name="group"
                                        lang={lang}
                                        value={groupId}
                                        values={groupsList}
                                        onChange={onGroupChange}
                                        onClick={onInputClick}
                                        onFocus={onInputClick}
                                        onKeyDown={onKeyDown}
                                    />
                                </Box>
                                <Box className="form-row">
                                    <SelectComponent
                                        label="objectType"
                                        name="object-type"
                                        lang={lang}
                                        value={type}
                                        values={types}
                                        onChange={onTypeChange}
                                        onClick={onInputClick}
                                        onFocus={onInputClick}
                                        onKeyDown={onKeyDown}
                                    />
                                </Box>
                                {additionalInfoElements}
                            </FormControl>
                            <Box className="edit-device__btn-bar">
                                <Button
                                    className="edit-device__btn"
                                    variant="contained"
                                    onClick={onSaveClick}
                                    onKeyDown={onBtnKeyDown}
                                >
                                    {getWord('save')}
                                </Button>
                                <Button
                                    className="edit-device__btn"
                                    variant="contained"
                                    color="error"
                                    onClick={onCancelClick}
                                    onKeyDown={onBtnKeyDown}
                                >
                                    {getWord('cancel')}
                                </Button>
                            </Box>
                        </>
                    )}
                    {loading && (
                        <Box className="edit-device__loader">
                            <CircularProgress size={50} />
                        </Box>
                    )}
                </Box>
            </Box>
            <Modal open={savedAlertVisible} onClose={onSaveAlertClose}>
                <SaveConfirmComponent
                    message="saveDescriptionAlertMessage"
                    lang={lang}
                    onClose={onSaveAlertClose}
                />
            </Modal>
        </>
    );
};

const EditDeviceComponent = React.memo(Component);

export default EditDeviceComponent;
